import { TopNav } from './TopNav';

const LAST_UPDATED = 'May 7, 2026';

export function Terms() {
  return (
    <div className="container narrow legal">
      <TopNav />
      <h1>Terms of Service</h1>
      <p className="legal-meta">Last updated: {LAST_UPDATED}</p>

      <p>
        These Terms of Service ("Terms") govern your access to and use of
        Vocal Cord (the "Service"), operated by{' '}
        <strong>Smotana</strong>, established in Canada ("we", "us"). By
        creating an account, calling our API, or otherwise using the
        Service, you agree to these Terms.
      </p>

      <h2>1. The Service</h2>
      <p>
        Vocal Cord provides a Model Context Protocol (MCP) relay that
        lets AI agents send messages to and receive replies from their
        human owner over channels the human has connected (e.g. email,
        Telegram, Discord, Slack, LINE, GitHub, Matrix). The Service
        delivers messages — it does not store them long-term, mine them,
        or train models on them. See our{' '}
        <a href="/privacy">Privacy Policy</a> for retention details.
      </p>

      <h2>2. Eligibility</h2>
      <p>
        You must be at least 16 years old (or the age of digital consent
        in your jurisdiction, whichever is higher) and able to enter
        into a binding contract. If you use Vocal Cord on behalf of an
        organisation, you represent that you have authority to bind it.
      </p>

      <h2>3. Account &amp; security</h2>
      <ul>
        <li>
          You are responsible for keeping your API key (and any per-channel
          bot tokens you provide) secret. Treat the API key like a
          password.
        </li>
        <li>
          You must promptly notify us at{' '}
          <a href="mailto:security@smotana.com">security@smotana.com</a>{' '}
          of any suspected compromise.
        </li>
        <li>
          We may rotate, suspend, or revoke API keys that show signs of
          abuse or compromise.
        </li>
      </ul>

      <h2>4. Plans, fees, and fair use</h2>
      <ul>
        <li>
          The <strong>Free</strong> plan is intended for hobby and
          occasional personal use, capped at 100 messages per calendar
          month.
        </li>
        <li>
          The <strong>Unlimited</strong> plan is billed annually at
          USD&nbsp;19.80 per year (USD&nbsp;1.65 / month equivalent).
          Prices are exclusive of any sales tax / VAT / GST that may
          apply.
        </li>
        <li>
          <strong>Fair-use limits.</strong> The Unlimited plan is intended
          for personal one-to-one communication between you and your AI
          agent. We apply a fair-use cap of 10,000 messages per calendar
          month and a per-minute rate limit. Operating bulk-messaging,
          broadcast, marketing, automated outreach, or multi-recipient
          flows is outside the intended scope and we may downgrade,
          throttle, suspend, or terminate accounts that violate fair use.
        </li>
        <li>
          Subscriptions auto-renew unless cancelled. You can cancel at any
          time from the dashboard's Stripe customer portal. Cancelling
          stops future renewals; we do not refund partial periods unless
          required by law.
        </li>
      </ul>

      <h2>5. Acceptable use</h2>
      <p>You agree not to use the Service to:</p>
      <ul>
        <li>
          Send unsolicited bulk messages, spam, phishing, or scam
          content.
        </li>
        <li>
          Harass, threaten, defame, or impersonate other people or
          organisations.
        </li>
        <li>
          Distribute malware, exploit vulnerabilities, or attempt to
          gain unauthorised access to any system.
        </li>
        <li>
          Send sexual content involving minors, or any content that is
          unlawful in your or the recipient's jurisdiction.
        </li>
        <li>
          Violate the terms of any channel platform you connect (e.g.
          Telegram's, Discord's, Slack's, Matrix's, or Email-provider
          terms). The Service is a relay — when you send through a
          third-party channel, you are also bound by that channel's
          rules.
        </li>
        <li>
          Circumvent rate limits, quotas, or fair-use enforcement.
        </li>
        <li>
          Scrape, mirror, or republish other users' data.
        </li>
      </ul>
      <p>
        Violations may result in immediate suspension or termination,
        with or without notice.
      </p>

      <h2>6. Your content</h2>
      <p>
        You retain ownership of the messages and data you send through
        the Service. You grant us a limited, non-exclusive licence to
        process, transmit, and briefly store that content solely for the
        purpose of delivering it on your behalf. We do not use your
        content to train AI models, sell it, or share it with third
        parties except the sub-processors listed in our{' '}
        <a href="/privacy">Privacy Policy</a>.
      </p>

      <h2>7. Third-party channels</h2>
      <p>
        Vocal Cord delivers messages to and from third-party platforms
        you choose to connect (e.g. Telegram, Discord, Slack, Email
        providers). Those platforms are independent services governed by
        their own terms and privacy policies. We are not responsible for
        their availability, behaviour, or content moderation decisions.
        Suspension by a third-party platform may interrupt delivery
        through that channel; we are not liable for losses caused by
        third-party outages.
      </p>

      <h2>8. Service availability</h2>
      <p>
        The Service is provided on an "as is" and "as available" basis.
        We do not guarantee uninterrupted operation. We may schedule
        maintenance, deploy changes, or take parts of the Service
        offline as needed. We will use reasonable efforts to minimise
        disruption.
      </p>

      <h2>9. Termination</h2>
      <ul>
        <li>
          You may delete your account at any time from the dashboard.
          Deletion triggers a 30-day tombstone period after which all
          personal data is permanently erased (see Privacy Policy §6).
        </li>
        <li>
          We may suspend or terminate your account for breach of these
          Terms, fair-use violations, abusive behaviour, non-payment,
          or to comply with law.
        </li>
        <li>
          Provisions that by their nature should survive termination
          (e.g. content licence to fulfil delivery already in flight,
          warranty disclaimers, liability limits) survive.
        </li>
      </ul>

      <h2>10. Disclaimers</h2>
      <p>
        TO THE MAXIMUM EXTENT PERMITTED BY LAW, THE SERVICE IS PROVIDED
        WITHOUT WARRANTY OF ANY KIND, EXPRESS OR IMPLIED, INCLUDING THE
        IMPLIED WARRANTIES OF MERCHANTABILITY, FITNESS FOR A PARTICULAR
        PURPOSE, AND NON-INFRINGEMENT. We do not warrant that messages
        will be delivered without delay or loss; delivery is best-effort
        through third-party platforms outside our control.
      </p>

      <h2>11. Limitation of liability</h2>
      <p>
        TO THE MAXIMUM EXTENT PERMITTED BY LAW, NEITHER PARTY WILL BE
        LIABLE FOR INDIRECT, INCIDENTAL, CONSEQUENTIAL, SPECIAL, OR
        PUNITIVE DAMAGES, OR FOR LOST PROFITS OR LOST DATA, ARISING OUT
        OF OR RELATED TO THE SERVICE. OUR TOTAL AGGREGATE LIABILITY FOR
        ANY CLAIM ARISING OUT OF OR RELATED TO THESE TERMS WILL NOT
        EXCEED THE GREATER OF (a)&nbsp;THE FEES YOU PAID US IN THE 12
        MONTHS BEFORE THE EVENT GIVING RISE TO THE CLAIM, OR
        (b)&nbsp;USD&nbsp;100. Some jurisdictions do not allow the
        exclusion or limitation of certain damages; in those
        jurisdictions, our liability is limited to the maximum extent
        permitted.
      </p>

      <h2>12. Indemnity</h2>
      <p>
        You agree to indemnify and hold us harmless from any claim by a
        third party arising out of (a)&nbsp;your messages or content,
        (b)&nbsp;your use of the Service in violation of these Terms or
        applicable law, or (c)&nbsp;your violation of any third-party
        platform's terms.
      </p>

      <h2>13. Changes to the Terms</h2>
      <p>
        We may update these Terms from time to time. Material changes
        will be announced via the dashboard and reflected by an updated
        "Last updated" date. Continued use of the Service after the
        change takes effect constitutes acceptance.
      </p>

      <h2>14. Governing law &amp; venue</h2>
      <p>
        These Terms are governed by the laws of the Province of British
        Columbia, Canada, without regard to its conflict-of-laws rules.
        Disputes will be brought exclusively in the courts of Vancouver,
        British Columbia, except where applicable consumer-protection
        law guarantees you a different forum.
      </p>

      <h2>15. Contact</h2>
      <p>
        Smotana<br />
        Legal: <a href="mailto:legal@smotana.com">legal@smotana.com</a><br />
        Privacy: <a href="mailto:privacy@smotana.com">privacy@smotana.com</a><br />
        Security: <a href="mailto:security@smotana.com">security@smotana.com</a>
      </p>

      <footer>
        <p>
          <a href="/">home</a> · <a href="/privacy">privacy</a>
        </p>
      </footer>
    </div>
  );
}
